/**
 * Ein Dialog für Pausieren und Fortsetzen — an beiden Stellen derselbe: in der
 * Veröffentlichungszentrale am Objekt und im Apartment-Workspace. Zwei Implementierungen wären
 * zwei Härtungen, von denen später eine zurückfällt.
 *
 * Asymmetrisch mit Absicht: **Pausieren** verlangt die Namenseingabe, weil es das Apartment aus
 * dem Verkauf nimmt — beim Kanalmanager über den vollen Horizont und intern für neue Buchungen.
 * **Fortsetzen** ist die Rückkehr in den Normalzustand und leitet die freien Nächte aus Buchungen
 * und Sperren neu ab; dafür genügt eine Rückfrage.
 */
import { useEffect, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { NameConfirmation, namesMatch } from './NameConfirmation';

export type LifecycleIntent = 'pause' | 'resume';

interface PauseResumeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intent: LifecycleIntent;
  propertyName: string;
  pending?: boolean;
  onConfirm: () => void | Promise<void>;
}

export function PauseResumeDialog({
  open,
  onOpenChange,
  intent,
  propertyName,
  pending,
  onConfirm,
}: PauseResumeDialogProps) {
  const [typed, setTyped] = useState('');
  const pausing = intent === 'pause';

  // Der Dialog wird pro Apartment wiederverwendet — ohne das Zurücksetzen trüge die Eingabe des
  // vorigen Apartments in den nächsten Aufruf und die Hürde wäre keine.
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const confirmDisabled = pending || (pausing && !namesMatch(propertyName, typed));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="bg-amber/10 text-amber mb-3 flex size-10 items-center justify-center rounded-full">
            {pausing ? <Pause size={18} aria-hidden /> : <Play size={18} aria-hidden />}
          </div>
          <DialogTitle>
            {pausing ? `„${propertyName}“ pausieren?` : `„${propertyName}“ fortsetzen?`}
          </DialogTitle>
          <DialogDescription>
            {pausing
              ? 'Das Apartment geht aus dem Verkauf: der Kanalmanager schließt jede Nacht über den ' +
                'gesamten Horizont, und es nimmt auch intern keine neuen Buchungen mehr an. ' +
                'Bestehende Buchungen bleiben unangetastet.'
              : 'Die freien Nächte werden aus Buchungen und Kalendersperren neu abgeleitet — ' +
                'belegte und gesperrte Nächte bleiben zu. Preise und Restriktionen sind unverändert.'}
          </DialogDescription>
        </DialogHeader>

        {pausing && (
          <DialogBody>
            <NameConfirmation
              expectedName={propertyName}
              value={typed}
              onChange={setTyped}
              disabled={pending}
            />
          </DialogBody>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Abbrechen
          </Button>
          <Button
            variant={pausing ? 'destructive' : 'brand'}
            loading={pending}
            disabled={confirmDisabled}
            onClick={() => void onConfirm()}
          >
            {pausing ? 'Apartment pausieren' : 'Apartment fortsetzen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
